import moment from "moment";

import { TSHIRT_SIZE_TO_NUMBER_SIZE } from "~/constants/size";
import { isDefined } from "~/shared/common";
import {
  EditorMode,
  LayoutKind,
  Priority,
  RelationshipKindKind,
  type StatusKind,
  TaskSourceKind,
  type TshirtSize,
  ViewKind,
  VisualizationItemType,
} from "~/shared/enums";
import type { Task, TaskAndUpdate, TaskUpdate } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { getPageDisplayName, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { formatDate } from "~/utils/date";
import { getOrdersBetween } from "~/utils/orderManager";
import { when } from "~/utils/wait";

import type { Actions } from "..";
import type { CurrentVisualizationRows } from "./types";

/** Task edit related actions */
export default (actions: Actions) => ({
  addTask: async (taskTitle: string, partialTask: Partial<Task> = {}) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();

    const dartboard = appStore.currentDartboardOrDefault;
    if (!dartboard) {
      return undefined;
    }

    const order = getOrdersBetween(undefined, dartboard.topTaskOrder)[0];
    const task = dataStore.createTask(taskTitle, dartboard.duid, order, TaskSourceKind.APP_QUICK_ADD, partialTask);
    appStore.cachedNewTaskInput = makeEmptyLexicalState();
    // TODO normalize filters at this point so that someone can see the task that they just made
    return task;
  },
  recommendProperties: () => {
    const pageStore = usePageStore();

    if (!pageStore.isOnline) {
      return;
    }

    const appStore = useAppStore();

    const currents = actions.visualization.getCurrentRows(true);
    if (currents.length !== 1) {
      return;
    }

    const current = currents[0];
    const task = current.data;
    if (!task) {
      return;
    }

    if (actions.visualization.currentRowIsCreateModal(currents)) {
      appStore.tcm?.generatePropertyRecommendations();
      return;
    }

    appStore.openTaskDetailForTask(task.duid);

    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      appStore.taskDetail?.generatePropertyRecommendations();
    }, 300);
  },
  recommendSubtasks: () => {
    const pageStore = usePageStore();

    if (!pageStore.isOnline) {
      return;
    }
    const appStore = useAppStore();

    const currents = actions.visualization.getCurrentRows(true);
    if (currents.length !== 1) {
      return;
    }

    const current = currents[0];
    const task = current.data;
    if (!task) {
      return;
    }

    if (actions.visualization.currentRowIsCreateModal(currents)) {
      appStore.tcm?.generateSubtaskRecommendations();
      return;
    }

    appStore.openTaskDetailForTask(task.duid);

    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      appStore.taskDetail?.generateSubtaskRecommendations();
    }, 300);
  },
  createSubtask: async (target?: Task, options?: { noDetail?: boolean }) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();

    const { noDetail = false } = options ?? {};

    const currents = actions.visualization.getCurrentRows(true);
    if (currents.length !== 1 && !target) {
      return;
    }

    const current = currents[0];
    const parentTask = target ?? (current.data ? await dataStore.getTaskByDuid(current.data.duid) : undefined);
    if (!parentTask) {
      return;
    }

    if (actions.visualization.currentRowIsCreateModal(currents)) {
      await appStore.tcm?.createSubtask();
      return;
    }

    if (!noDetail && appStore.taskDetailOpen) {
      appStore.taskDetail?.createSubtask();
      return;
    }

    const parentKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;

    const partialTask: Partial<Task> = {
      drafterDuid: parentTask.drafterDuid,
      relationships: [
        {
          duid: makeDuid(),
          kindDuid: parentKindDuid,
          targetDuid: parentTask.duid,
          isForward: false,
        },
      ],
    };

    actions.visualization.createTaskUnderneath(partialTask, { makeSubtask: true });
  },
  convertToNormalTask: (subtask: Task) => {
    const dataStore = useDataStore();

    const parentTask = dataStore.getParentTaskIfLoaded(subtask);
    if (!parentTask) {
      return;
    }

    const relationshipDuid = subtask.relationships.find((e) => e.targetDuid === parentTask.duid)?.duid;
    if (!relationshipDuid) {
      return;
    }

    dataStore.deleteRelationship(subtask.duid, relationshipDuid);
  },
  addAttachments: () => {
    const appStore = useAppStore();

    const currents = actions.visualization.getCurrentRows(true);
    if (currents.length !== 1) {
      return;
    }

    const current = currents[0];
    const task = current.data;
    if (!task) {
      return;
    }

    if (actions.visualization.currentRowIsCreateModal(currents)) {
      appStore.tcm?.openFilePicker();
      return;
    }

    appStore.openTaskDetailForTask(task.duid);

    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      appStore.taskDetail?.openFilePicker();
    }, 300);
  },
  addLink: () => {
    const appStore = useAppStore();

    const currents = actions.visualization.getCurrentRows(true);
    if (currents.length !== 1) {
      return;
    }

    const current = currents[0];
    const task = current.data;
    if (!task) {
      return;
    }

    if (actions.visualization.currentRowIsCreateModal(currents)) {
      return;
    }

    appStore.openTaskDetailForTask(task.duid);
    appStore.setLinkOpenInModal({ taskDuid: task.duid });
  },
  setTags: (tagDuids: string[]) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }

    currents.forEach((current) => {
      if (!current?.data) {
        return;
      }
      dataStore.replaceOptions(dataStore.defaultTagsProperty, [current.data.duid], tagDuids);
    });
  },
  setAssignees: (assigneeDuids: string[]) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }

    currents.forEach((current) => {
      if (!current?.data) {
        return;
      }
      dataStore.replaceUsers(dataStore.defaultAssigneesProperty, [current.data.duid], assigneeDuids);
    });
  },
  setAssignee: (newAssigneeDuid: string | null) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }

    const newAssigneeDuids = newAssigneeDuid === null ? [] : [newAssigneeDuid];
    currents.forEach((current) => {
      if (!current?.data) {
        return;
      }
      dataStore.replaceUsers(dataStore.defaultAssigneesProperty, [current.data.duid], newAssigneeDuids);
    });
  },
  setReviewers: (reviewerDuids: string[]) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }

    currents.forEach((current) => {
      if (!current?.data) {
        return;
      }
      dataStore.replaceUsers(dataStore.defaultReviewersProperty, [current.data.duid], reviewerDuids);
    });
  },
  setReviewer: (newReviewerDuid: string | null) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }

    const newReviewerDuids = newReviewerDuid === null ? [] : [newReviewerDuid];
    currents.forEach((current) => {
      if (!current?.data) {
        return;
      }
      dataStore.replaceUsers(dataStore.defaultReviewersProperty, [current.data.duid], newReviewerDuids);
    });
  },
  setStartAt: async (startAt: Date) => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        startAt: formatDate(startAt),
      }))
    );
  },
  setDueAt: async (dueAt: Date) => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        dueAt: formatDate(dueAt),
      }))
    );
  },
  setPriority: async (priority: Priority | null) => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        priority,
      }))
    );
  },
  setSize: async (size: string | null) => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    const sizeNum = TSHIRT_SIZE_TO_NUMBER_SIZE.get(size as TshirtSize) ?? null;

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        size: sizeNum,
      }))
    );
  },
  setRemindAt: async (remindAt: Date) => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    const newRemindAt = moment(remindAt).toISOString();

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        remindAt: newRemindAt,
      }))
    );
  },
  setStatusWithDuid: async (currents: CurrentVisualizationRows, newStatusDuid: string) => {
    const dataStore = useDataStore();

    const tasks = (
      await dataStore.getTasksByDuids(
        currents
          .map((e) =>
            e.data?.itemType === VisualizationItemType.TASK || e.data?.itemType === VisualizationItemType.CREATE_MODAL
              ? e.data.duid
              : null
          )
          .filter(isDefined),
        { includeTrashed: false, includeDraft: true }
      )
    ).filter((e) => e.statusDuid !== newStatusDuid);
    if (tasks.length === 0) {
      return;
    }

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        statusDuid: newStatusDuid,
      }))
    );
  },
  cycleStatuses: async (statusKind: StatusKind) => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }
    const statusesWithKind = dataStore.getStatusesByKinds([statusKind], dataStore.defaultStatusProperty);
    if (statusesWithKind.length === 0) {
      return;
    }
    const tasks = await actions.visualization.getCurrentRowsTasks();
    const taskStatusDuids = Array.from(new Set(tasks.map((e) => e.statusDuid)));
    const newStatusIndex =
      taskStatusDuids.length === 1
        ? (statusesWithKind.findIndex((e) => e.duid === taskStatusDuids[0]) + 1) % statusesWithKind.length
        : 0;
    const newStatusDuid = statusesWithKind[newStatusIndex].duid;
    await actions.visualization.setStatusWithDuid(currents, newStatusDuid);
  },
  setStatus: async (newStatusDuid: string) => {
    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }
    await actions.visualization.setStatusWithDuid(currents, newStatusDuid);
  },
  setStatusDone: async () => {
    const dataStore = useDataStore();

    const currents = actions.visualization.getCurrentRows();
    if (currents.length === 0) {
      return;
    }
    const newStatusDuid = dataStore.defaultDefaultFinishedStatus?.duid;
    if (!newStatusDuid) {
      return;
    }
    await actions.visualization.setStatusWithDuid(currents, newStatusDuid);
  },
  replicateTasks: async (tasks?: Task[]) => {
    const dataStore = useDataStore();

    const selectedTasks = tasks ?? (await actions.visualization.getCurrentRowsTasks());
    if (selectedTasks.length === 0) {
      return;
    }
    const allTasks = dataStore.getLoadedTasksForPageDuidOrdered(selectedTasks[0].dartboardDuid);

    const lastTask = selectedTasks[selectedTasks.length - 1];
    const lastTaskIndex = allTasks.findIndex((e: Task) => e.duid === lastTask.duid);
    const belowTaskOrder = allTasks[lastTaskIndex + 1]?.order;
    const replicatesOrders = getOrdersBetween(lastTask.order, belowTaskOrder, selectedTasks.length);

    const tasksToReplicate: TaskAndUpdate[] = selectedTasks.map((task, index) => ({
      task,
      order: replicatesOrders[index] ?? "",
    }));

    const replicatedParents = await dataStore.replicateTasks(tasksToReplicate);
    if (replicatedParents.length > 1) {
      return;
    }

    actions.visualization.navigateToTask(replicatedParents[0].duid);
  },
  trashTasks: async (tasks?: Task[]) => {
    const dataStore = useDataStore();

    const selectedTasks = tasks ?? (await actions.visualization.getCurrentRowsTasks());
    if (selectedTasks.length === 0) {
      return;
    }

    // Delete empty tasks
    const tasksToTrash: Task[] = [];
    const tasksToDelete: Task[] = [];

    selectedTasks.forEach((task) => {
      if (dataStore.isTaskEmpty(task) || task.inTrash) {
        tasksToDelete.push(task);
      } else {
        tasksToTrash.push(task);
      }
    });

    dataStore.deleteTasks(tasksToDelete);

    dataStore.updateTasks(
      tasksToTrash.map((e) => ({
        duid: e.duid,
        inTrash: true,
      })),
      {
        makeDescription: (isActive, content) => `mov${isActive ? "ing" : "ed"} ${content} to the trash`,
      }
    );
  },
  restoreTasks: async () => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    dataStore.updateTasks(
      tasks.map((e) => ({
        duid: e.duid,
        inTrash: false,
      })),
      {
        makeDescription: (isActive, content) => `${isActive ? "restoring" : "restored"} ${content} from the trash`,
      }
    );
  },
  deleteTasks: async () => {
    const dataStore = useDataStore();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }
    dataStore.deleteTasks(tasks);
  },
  moveTasksToDartboard: async (dartboardDuid: string, providedTasks?: Task[]) => {
    const dataStore = useDataStore();

    const dartboard = dataStore.getDartboardByDuid(dartboardDuid);
    if (!dartboard) {
      return;
    }

    const tasks = providedTasks ?? (await actions.visualization.getCurrentRowsTasks());
    if (tasks.length === 0) {
      return;
    }

    const orders = getOrdersBetween(undefined, dartboard.topTaskOrder, tasks.length);
    dataStore.updateTasks(
      tasks.map((e, i) => {
        const update: TaskUpdate = {
          duid: e.duid,
          dartboardDuid,
          order: orders[i],
        };
        if (e.inTrash) {
          update.inTrash = false;
        }
        return update;
      }),
      {
        makeDescription: (isActive, content) =>
          `mov${isActive ? "ing" : "ed"} ${content} to ${getPageDisplayName(dartboard, dataStore.getSpaceByDuid)}`,
      }
    );
  },
  moveTasksToDartboardOrTrash: async (pageDuid: string, providedTasks?: Task[]) => {
    const dataStore = useDataStore();
    if (pageDuid === dataStore.getViewByKind(ViewKind.TRASH)?.duid) {
      await actions.visualization.trashTasks();
      return;
    }

    await actions.visualization.moveTasksToDartboard(pageDuid, providedTasks);
  },
  createTaskUnderneath: async (
    partialTask?: Partial<Task>,
    options?: { makeSubtask?: boolean; preventStartEditing?: boolean }
  ) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const tenantStore = useTenantStore();
    const visualization = appStore.getActiveVisualization();

    const { makeSubtask = false, preventStartEditing = false } = options ?? {};

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length !== 1) {
      return undefined;
    }
    const task = tasks[0];
    const taskRow = visualization.getRow(task.duid);
    if (!taskRow || taskRow.rowIndex === null) {
      return undefined;
    }

    const rowBelow = visualization.getRowByIndex(taskRow.rowIndex + 1);
    const taskBelow = rowBelow?.data ? await dataStore.getTaskByDuid(rowBelow.data.duid) : null;
    let orderOfTaskBelow = taskBelow?.order;

    const parentKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    const oldParentRelationship = dataStore.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false)[0];
    const parent = makeSubtask
      ? task
      : oldParentRelationship
        ? await dataStore.getTaskByDuid(oldParentRelationship.targetDuid)
        : undefined;

    const partialTaskNorm: Partial<Task> = partialTask ?? {};
    if (parent) {
      partialTaskNorm.drafterDuid = parent.drafterDuid;

      if (taskBelow && parent.duid !== dataStore.getParentTaskIfLoaded(taskBelow)?.duid) {
        orderOfTaskBelow = undefined;
      }
    }

    const orderOfNewTask = getOrdersBetween(task.order, orderOfTaskBelow)[0];

    let newTask: Task;
    if (parent && tenantStore.copyParentFieldsOnCreate) {
      partialTaskNorm.title = "";
      partialTaskNorm.description = makeEmptyLexicalState();
      newTask = (
        await dataStore.replicateTasks(
          [
            {
              task,
              order: orderOfNewTask,
              partialTask: partialTaskNorm,
            },
          ],
          { propertiesAndParentOnly: true, resetSize: true }
        )
      )[0];
    } else {
      if (parent) {
        partialTaskNorm.relationships = [
          {
            duid: makeDuid(),
            kindDuid: parentKindDuid,
            targetDuid: parent.duid,
            isForward: false,
          },
        ];
      }
      newTask = await dataStore.createTask(
        "",
        task.dartboardDuid,
        orderOfNewTask,
        TaskSourceKind.APP_ENTER,
        partialTaskNorm
      );
    }

    await when(() => !!visualization.getRow(newTask.duid));
    visualization.selectAndScrollTo(newTask.duid);
    if (!preventStartEditing) {
      actions.visualization.openKeyboardIfIos();
      visualization.editTitle(newTask.duid);
    }

    return newTask;
  },
  subtaskIndent: async (partialTask?: Partial<Task>) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const visualization = appStore.getActiveVisualization();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    // Coalesce subtasks into their parents if both are selected
    const selectedTaskDuids = new Set(tasks.map((t) => t.duid));
    const topLevelTasks = tasks.filter((task) => {
      const parent = dataStore.getParentTaskIfLoaded(task);
      return !parent || !selectedTaskDuids.has(parent.duid);
    });

    // Check if remaining top-level tasks are all siblings
    const firstParent = dataStore.getParentTaskIfLoaded(topLevelTasks[0]);
    const allAreSiblings = topLevelTasks.every(
      (task) => dataStore.getParentTaskIfLoaded(task)?.duid === firstParent?.duid
    );
    if (topLevelTasks.length > 1 && !allAreSiblings) {
      return;
    }

    // Get siblings from visualization's view (maintains proper order)
    const taskRow = visualization.getRow(topLevelTasks[0].duid);
    const isBoard = appStore.layout?.kind === LayoutKind.BOARD;

    const siblingItems = firstParent
      ? visualization.getChildren(firstParent.duid)
      : isBoard
        ? visualization.getChildren(null)
        : (taskRow?.parent?.childrenAfterFilter?.map((e) => e?.data)?.filter(isDefined) ?? []);

    const taskIndices = new Map<string, number>();
    let topMostTask: Task | undefined;
    let minRowIndex = Number.MAX_SAFE_INTEGER;

    topLevelTasks.forEach((task) => {
      const rowIndex = visualization.getRow(task.duid)?.rowIndex;
      if (rowIndex != null) {
        taskIndices.set(task.duid, rowIndex);
        if (rowIndex < minRowIndex) {
          minRowIndex = rowIndex;
          topMostTask = task;
        }
      }
    });

    if (!topMostTask) {
      return;
    }

    const topMostTaskDuid = topMostTask.duid;
    const topMostSiblingIndex = siblingItems.findIndex((item) => item.duid === topMostTaskDuid);
    if (topMostSiblingIndex <= 0) {
      return;
    }

    const newParent = await dataStore.getTaskByDuid(siblingItems[topMostSiblingIndex - 1].duid);
    if (!newParent) {
      return;
    }
    const newSiblings = await dataStore.getTasksByDuidsOrdered(
      dataStore.getRelationshipsByKindKind(newParent, RelationshipKindKind.PARENT_OF, true).map((e) => e.targetDuid),
      { includeTrashed: false, includeDraft: false }
    );

    const startOrder = newSiblings[newSiblings.length - 1]?.order;
    const newOrders = getOrdersBetween(startOrder, undefined, topLevelTasks.length);

    const sortedTasks = [...topLevelTasks].sort(
      (a, b) =>
        (taskIndices.get(a.duid) ?? Number.MAX_SAFE_INTEGER) - (taskIndices.get(b.duid) ?? Number.MAX_SAFE_INTEGER)
    );

    const updates = sortedTasks.map((task, index) => ({
      task,
      order: newOrders[index] || task.order,
      partialTask,
    }));
    dataStore.changeParent(updates, newParent);
  },
  subtaskOutdent: async (partialTask?: Partial<Task>) => {
    const appStore = useAppStore();
    const dataStore = useDataStore();
    const visualization = appStore.getActiveVisualization();

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return;
    }

    // Coalesce subtasks into their parents if both are selected
    const selectedTaskDuids = new Set(tasks.map((t) => t.duid));
    const topLevelTasks = tasks.filter((task) => {
      const parent = dataStore.getParentTaskIfLoaded(task);
      return !parent || !selectedTaskDuids.has(parent.duid);
    });

    const firstTask = topLevelTasks[0];
    const parent = dataStore.getParentTaskIfLoaded(firstTask);
    if (!parent) {
      return;
    }

    const allHaveSameParent = topLevelTasks.every(
      (task) => dataStore.getParentTaskIfLoaded(task)?.duid === parent.duid
    );
    if (topLevelTasks.length > 1 && !allHaveSameParent) {
      return;
    }

    const grandparent = dataStore.getParentTaskIfLoaded(parent);
    // Determine the correct container for siblings one level up.
    // If no grandparent (moving to top-level), use the parent's group container.
    const parentRow = visualization.getRow(parent.duid);
    const upOneLevelSiblingItems = grandparent
      ? visualization.getChildren(grandparent.duid)
      : (parentRow?.parent?.childrenAfterFilter?.map((e) => e?.data)?.filter(isDefined) ?? []);

    const parentIndex = upOneLevelSiblingItems.findIndex((item) => item.duid === parent.duid);

    const taskIndices = new Map<string, number>();
    topLevelTasks.forEach((task) => {
      const rowIndex = visualization.getRow(task.duid)?.rowIndex;
      if (rowIndex != null) {
        taskIndices.set(task.duid, rowIndex);
      }
    });

    const sortedTasks = [...topLevelTasks].sort(
      (a, b) =>
        (taskIndices.get(a.duid) ?? Number.MAX_SAFE_INTEGER) - (taskIndices.get(b.duid) ?? Number.MAX_SAFE_INTEGER)
    );

    const nextSiblingItem = upOneLevelSiblingItems[parentIndex + 1];
    const nextOrder = nextSiblingItem ? (await dataStore.getTaskByDuid(nextSiblingItem.duid))?.order : undefined;
    const newOrders = getOrdersBetween(parent.order, nextOrder, sortedTasks.length);

    const updates = sortedTasks.map((task, index) => ({
      task,
      order: newOrders[index] || task.order,
      partialTask,
    }));
    dataStore.changeParent(updates, grandparent ?? null);
  },
  showFeedbackTooltipForTask: (
    taskDuids: string[],
    recommendationDuids: string[],
    editorMode: EditorMode,
    tooltipNode?: Element | null
  ) => {
    let htmlNode: Element | null = tooltipNode ?? null;
    const appStore = useAppStore();
    const visualization = appStore.getActiveVisualization();

    if (editorMode === EditorMode.CONTEXT_MENU) {
      // For the context menu we want the tooltip to show on the last selected task
      htmlNode = visualization.getHtml(taskDuids[taskDuids.length - 1]);
    }

    if (!htmlNode) {
      return;
    }
    appStore.showFeedbackTooltip(htmlNode, recommendationDuids);
  },
});
